import { layersApi, Layer } from './api';

/** Local layer settings stored in localStorage (opacity, visibility) */
interface LocalLayerSettings {
  [layerId: string]: {
    visible?: boolean;
    opacity?: number;
  };
}

/**
 * Manages layers for a map
 * Handles loading, saving, and manipulating layers
 * Note: opacity and visibility are stored locally (localStorage), not on the server
 */
export class LayerManager {
  private mapId: string;
  private layers: Layer[] = [];
  private activeLayerId: string = 'default';
  private onLayersChanged: (() => void) | null = null;
  private onActiveLayerChanged: ((layerId: string) => void) | null = null;
  private onSaveStateChanged: ((isSaving: boolean, hasUnsaved: boolean) => void) | null = null;
  private onLayerIdsMapped: ((idMapping: Map<string, string>) => void) | null = null;
  private dirtyLayers: Set<string> = new Set();
  private newLayers: Set<string> = new Set();
  private deletedLayers: Set<string> = new Set();
  private saveTimeout: NodeJS.Timeout | null = null;
  private isSaving: boolean = false;
  private pendingSaveAfterCurrent: boolean = false;
  private currentSavePromise: Promise<void> | null = null;
  private nextLocalId: number = 1;
  private autoSaveEnabled: boolean = true;

  private static readonly SAVE_DELAY = 1000;
  private static readonly LOCAL_STORAGE_KEY_PREFIX = 'mapbuilder_layer_settings_';

  /** The default layer is virtual (client-side only, never saved to server) */
  private static readonly DEFAULT_LAYER: Layer = {
    id: 'default',
    name: 'Default',
    visible: true,
    order: -1, // Always at the bottom
    opacity: 100,
    parentId: null,
    description: ''
  };

  constructor(mapId: string) {
    this.mapId = mapId;
  }

  /**
   * Get localStorage key for this map's layer settings
   */
  private getLocalStorageKey(): string {
    return `${LayerManager.LOCAL_STORAGE_KEY_PREFIX}${this.mapId}`;
  }

  /**
   * Load local layer settings from localStorage
   */
  private loadLocalSettings(): LocalLayerSettings {
    try {
      const stored = localStorage.getItem(this.getLocalStorageKey());
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Failed to load local layer settings:', err);
    }
    return {};
  }

  /**
   * Save local layer settings to localStorage
   */
  private saveLocalSettings(): void {
    try {
      const settings: LocalLayerSettings = {};
      for (const layer of this.layers) {
        settings[layer.id] = {
          visible: layer.visible,
          opacity: layer.opacity
        };
      }
      localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(settings));
    } catch (err) {
      console.error('Failed to save local layer settings:', err);
    }
  }

  /**
   * Apply local settings to layers
   */
  private applyLocalSettings(localSettings: LocalLayerSettings): void {
    for (const layer of this.layers) {
      const settings = localSettings[layer.id];
      if (settings) {
        if (settings.visible !== undefined) {
          layer.visible = settings.visible;
        }
        if (settings.opacity !== undefined) {
          layer.opacity = settings.opacity;
        }
      }
    }
  }

  /**
   * Set callback for when layers change
   */
  setOnLayersChanged(callback: (() => void) | null): void {
    this.onLayersChanged = callback;
  }

  /**
   * Set callback for when active layer changes
   */
  setOnActiveLayerChanged(callback: ((layerId: string) => void) | null): void {
    this.onActiveLayerChanged = callback;
  }

  /**
   * Set callback for when save state changes (isSaving, hasUnsavedChanges)
   */
  setOnSaveStateChanged(callback: ((isSaving: boolean, hasUnsaved: boolean) => void) | null): void {
    this.onSaveStateChanged = callback;
  }

  /**
   * Set callback for when local layer IDs are mapped to server IDs after save
   * This is used to update layerIds in props and texts
   */
  setOnLayerIdsMapped(callback: ((idMapping: Map<string, string>) => void) | null): void {
    this.onLayerIdsMapped = callback;
  }

  /**
   * Set whether auto-save is enabled
   * When disabled, changes will only be saved when forceSave is called
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
  }

  /**
   * Load all layers for the map
   * The default layer is always injected client-side and never comes from the server
   */
  async loadLayers(): Promise<void> {
    try {
      const response = await layersApi.list(this.mapId);
      // Filter out any 'default' layer from server (should not exist, but just in case)
      this.layers = response.layers.filter(l => l.id !== 'default');

      // Set default values for visibility, opacity, parentId, and description (visibility/opacity will be overridden by local settings)
      this.layers = this.layers.map(l => ({
        ...l,
        visible: true,
        opacity: 100,
        parentId: l.parentId ?? null,
        description: l.description ?? ''
      }));

      // Always inject the virtual default layer (client-side only)
      this.layers.unshift({ ...LayerManager.DEFAULT_LAYER });

      // Load and apply local settings (opacity, visibility)
      const localSettings = this.loadLocalSettings();
      this.applyLocalSettings(localSettings);

      // Sort by order (default layer has order -1, so it stays at bottom)
      this.layers.sort((a, b) => a.order - b.order);

      // Set active layer to first visible layer, or default layer
      const firstVisible = this.layers.find(l => l.visible);
      this.activeLayerId = firstVisible?.id || 'default';

      this.notifyLayersChanged();
    } catch (err) {
      console.error('Failed to load layers:', err);
      // Initialize with default layer on error
      this.layers = [{ ...LayerManager.DEFAULT_LAYER }];
      this.activeLayerId = 'default';
    }
  }

  /**
   * Get all layers sorted by order
   */
  getLayers(): Layer[] {
    return [...this.layers].sort((a, b) => a.order - b.order);
  }

  /**
   * Get a single layer by ID
   */
  getLayer(layerId: string): Layer | undefined {
    return this.layers.find(l => l.id === layerId);
  }

  /**
   * Get the active layer ID
   */
  getActiveLayerId(): string {
    return this.activeLayerId;
  }

  /**
   * Get the active layer
   */
  getActiveLayer(): Layer | undefined {
    return this.layers.find(l => l.id === this.activeLayerId);
  }

  /**
   * Set the active layer
   */
  setActiveLayer(layerId: string): void {
    const layer = this.layers.find(l => l.id === layerId);
    if (layer) {
      this.activeLayerId = layerId;
      this.onActiveLayerChanged?.(layerId);
    }
  }

  /**
   * Check if a layer is visible (including parent visibility)
   * A layer is only visible if it and all its ancestors are visible
   */
  isLayerVisible(layerId: string): boolean {
    const layer = this.layers.find(l => l.id === layerId);
    if (!layer) return true;

    // If this layer is hidden, return false
    if (!layer.visible) return false;

    // Check parent visibility recursively
    if (layer.parentId) {
      return this.isLayerVisible(layer.parentId);
    }

    return true;
  }

  /**
   * Check if a layer's own visibility is enabled (ignoring parent state)
   */
  isLayerOwnVisible(layerId: string): boolean {
    const layer = this.layers.find(l => l.id === layerId);
    return layer?.visible ?? true;
  }

  /**
   * Add a new layer locally (will be saved on next save)
   * @param name Layer name
   * @param parentId Optional parent layer ID for nesting
   */
  addLayer(name: string, parentId?: string | null): Layer {
    const localId = `local_layer_${Date.now()}_${this.nextLocalId++}`;

    // Validate parent exists if provided
    if (parentId && !this.layers.find(l => l.id === parentId)) {
      parentId = null;
    }

    // Calculate next order among siblings (same parent)
    const siblings = this.layers.filter(l => l.parentId === (parentId || null));
    const maxOrder = siblings.reduce((max, l) => Math.max(max, l.order), -1);

    const layer: Layer = {
      id: localId,
      name,
      visible: true,
      order: maxOrder + 1,
      opacity: 100,
      parentId: parentId || null,
      description: ''
    };

    this.layers.push(layer);
    this.newLayers.add(localId);
    this.scheduleSave();
    this.saveLocalSettings();
    this.notifyLayersChanged();

    return layer;
  }

  /**
   * Update a layer locally (with debounced save for server-side properties)
   * Note: The default layer is virtual and server-side properties cannot be changed
   */
  updateLayer(layerId: string, updates: Partial<Pick<Layer, 'name' | 'visible' | 'order' | 'opacity' | 'parentId' | 'description'>>): void {
    const layer = this.layers.find(l => l.id === layerId);
    if (!layer) return;

    const isDefaultLayer = layerId === 'default';
    let needsServerSave = false;

    // Server-side properties (ignored for default layer which is virtual)
    if (!isDefaultLayer) {
      if (updates.name !== undefined) {
        layer.name = updates.name;
        needsServerSave = true;
      }
      if (updates.order !== undefined) {
        layer.order = updates.order;
        needsServerSave = true;
      }
      if (updates.parentId !== undefined) {
        // Check for cycles before setting parent
        if (!this.wouldCreateCycle(layerId, updates.parentId)) {
          layer.parentId = updates.parentId;
          needsServerSave = true;
        }
      }
      if (updates.description !== undefined) {
        layer.description = updates.description;
        needsServerSave = true;
      }
    }

    // Local-only properties (stored in localStorage) - allowed for all layers including default
    if (updates.visible !== undefined) {
      layer.visible = updates.visible;
      this.saveLocalSettings();
    }
    if (updates.opacity !== undefined) {
      layer.opacity = updates.opacity;
      this.saveLocalSettings();
    }

    if (needsServerSave && !this.newLayers.has(layerId)) {
      this.dirtyLayers.add(layerId);
      this.scheduleSave();
    }

    this.notifyLayersChanged();
  }

  /**
   * Check if moving a layer to a parent would create a cycle
   */
  private wouldCreateCycle(layerId: string, newParentId: string | null): boolean {
    if (!newParentId) return false;
    if (newParentId === layerId) return true;

    let currentId: string | null = newParentId;
    const visited = new Set<string>();

    while (currentId) {
      if (visited.has(currentId)) return true;
      if (currentId === layerId) return true;
      visited.add(currentId);

      const parent = this.layers.find(l => l.id === currentId);
      currentId = parent?.parentId ?? null;
    }

    return false;
  }

  /**
   * Move a layer to a new parent
   * @param layerId The layer to move
   * @param newParentId The new parent layer ID (null for root)
   */
  moveLayerToParent(layerId: string, newParentId: string | null): boolean {
    // Cannot move the default layer (it's virtual)
    if (layerId === 'default') return false;

    const layer = this.layers.find(l => l.id === layerId);
    if (!layer) return false;

    // Check for cycles
    if (this.wouldCreateCycle(layerId, newParentId)) {
      return false;
    }

    // Calculate order among new siblings
    const siblings = this.layers.filter(l => l.parentId === newParentId && l.id !== layerId);
    const maxOrder = siblings.reduce((max, l) => Math.max(max, l.order), -1);

    layer.parentId = newParentId;
    layer.order = maxOrder + 1;

    if (!this.newLayers.has(layerId)) {
      this.dirtyLayers.add(layerId);
    }

    this.scheduleSave();
    this.notifyLayersChanged();
    return true;
  }

  /**
   * Get all child layers of a parent (including nested children)
   */
  getDescendants(layerId: string): Layer[] {
    const descendants: Layer[] = [];
    const directChildren = this.layers.filter(l => l.parentId === layerId);

    for (const child of directChildren) {
      descendants.push(child);
      descendants.push(...this.getDescendants(child.id));
    }

    return descendants;
  }

  /**
   * Get direct children of a layer
   */
  getChildren(layerId: string | null): Layer[] {
    return this.layers.filter(l => l.parentId === layerId).sort((a, b) => a.order - b.order);
  }

  /**
   * Get root layers (no parent)
   */
  getRootLayers(): Layer[] {
    return this.layers.filter(l => l.parentId === null).sort((a, b) => a.order - b.order);
  }

  /**
   * Update layer opacity (local only - stored in localStorage)
   * @param layerId The layer ID
   * @param opacity Opacity value from 0 to 100
   */
  updateLayerOpacity(layerId: string, opacity: number): void {
    const layer = this.layers.find(l => l.id === layerId);
    if (!layer) return;

    layer.opacity = Math.max(0, Math.min(100, opacity));
    this.saveLocalSettings();
    this.notifyLayersChanged();
  }

  /**
   * Toggle layer visibility (local only - stored in localStorage)
   */
  toggleLayerVisibility(layerId: string): void {
    const layer = this.layers.find(l => l.id === layerId);
    if (!layer) return;

    layer.visible = !layer.visible;
    this.saveLocalSettings();
    this.notifyLayersChanged();
  }

  /**
   * Reorder layers
   * @param layerIds Array of layer IDs in the new order (excluding default layer)
   */
  reorderLayers(layerIds: string[]): void {
    // Filter out default layer from reordering (it's virtual and always at order -1)
    const filteredIds = layerIds.filter(id => id !== 'default');

    // Update order for each layer
    for (let i = 0; i < filteredIds.length; i++) {
      const layer = this.layers.find(l => l.id === filteredIds[i]);
      if (layer) {
        layer.order = i;
        if (!this.newLayers.has(layer.id)) {
          this.dirtyLayers.add(layer.id);
        }
      }
    }

    this.layers.sort((a, b) => a.order - b.order);
    this.scheduleSave();
    this.notifyLayersChanged();
  }

  /**
   * Move a layer up or down
   */
  moveLayer(layerId: string, direction: 'up' | 'down'): void {
    // Cannot move the default layer (it's virtual)
    if (layerId === 'default') return;

    const sortedLayers = this.getLayers();
    const index = sortedLayers.findIndex(l => l.id === layerId);

    if (index === -1) return;

    // 'up' means higher in the visual stack (higher order)
    // 'down' means lower in the visual stack (lower order)
    const targetIndex = direction === 'up' ? index + 1 : index - 1;

    if (targetIndex < 0 || targetIndex >= sortedLayers.length) return;

    // Swap orders
    const currentOrder = sortedLayers[index].order;
    const targetOrder = sortedLayers[targetIndex].order;

    sortedLayers[index].order = targetOrder;
    sortedLayers[targetIndex].order = currentOrder;

    if (!this.newLayers.has(sortedLayers[index].id)) {
      this.dirtyLayers.add(sortedLayers[index].id);
    }
    if (!this.newLayers.has(sortedLayers[targetIndex].id)) {
      this.dirtyLayers.add(sortedLayers[targetIndex].id);
    }

    this.layers.sort((a, b) => a.order - b.order);
    this.scheduleSave();
    this.notifyLayersChanged();
  }

  /**
   * Delete a layer locally
   * Returns info about deleted layers and the target layer for props
   * @param layerId The layer to delete
   * @param movePropsToLayerId The layer to move props to (required if layer has props)
   * @param deleteChildren If true, also delete all child layers
   */
  deleteLayer(
    layerId: string,
    movePropsToLayerId?: string,
    deleteChildren: boolean = false
  ): { targetLayerId: string | null; deletedLayerIds: string[] } | null {
    // Cannot delete the default layer
    if (layerId === 'default') {
      return null;
    }

    // Get all layers to delete
    const layersToDelete: string[] = [layerId];
    if (deleteChildren) {
      const descendants = this.getDescendants(layerId);
      layersToDelete.push(...descendants.map(l => l.id));
    }

    // Cannot delete if it would remove all layers
    if (this.layers.length <= layersToDelete.length) {
      return null;
    }

    const layerIndex = this.layers.findIndex(l => l.id === layerId);
    if (layerIndex === -1) return null;

    const deletedLayer = this.layers[layerIndex];

    // Determine target layer for props
    let targetLayerId: string | null = movePropsToLayerId || null;
    if (!targetLayerId) {
      // Default to first layer that isn't being deleted
      const otherLayer = this.layers.find(l => !layersToDelete.includes(l.id));
      targetLayerId = otherLayer?.id || null;
    }

    if (!deleteChildren) {
      // Move children to deleted layer's parent
      for (const layer of this.layers) {
        if (layer.parentId === layerId) {
          layer.parentId = deletedLayer.parentId;
          if (!this.newLayers.has(layer.id)) {
            this.dirtyLayers.add(layer.id);
          }
        }
      }
    }

    // Remove layers (in reverse order to handle indices correctly)
    for (const deleteId of layersToDelete) {
      const idx = this.layers.findIndex(l => l.id === deleteId);
      if (idx !== -1) {
        this.layers.splice(idx, 1);
        this.dirtyLayers.delete(deleteId);

        if (this.newLayers.has(deleteId)) {
          this.newLayers.delete(deleteId);
        } else {
          this.deletedLayers.add(deleteId);
        }
      }
    }

    // Renormalize orders within each parent group
    const parents = [...new Set(this.layers.map(l => l.parentId))];
    for (const parentId of parents) {
      const siblings = this.layers
        .filter(l => l.parentId === parentId)
        .sort((a, b) => a.order - b.order);
      siblings.forEach((l, i) => {
        if (l.order !== i) {
          l.order = i;
          if (!this.newLayers.has(l.id)) {
            this.dirtyLayers.add(l.id);
          }
        }
      });
    }

    // If deleted layer was active, switch to another
    if (layersToDelete.includes(this.activeLayerId) && this.layers.length > 0) {
      this.activeLayerId = this.layers[0].id;
      this.onActiveLayerChanged?.(this.activeLayerId);
    }

    this.scheduleSave();
    this.saveLocalSettings();
    this.notifyLayersChanged();

    return { targetLayerId, deletedLayerIds: layersToDelete };
  }

  /**
   * Get the number of layers
   */
  getLayerCount(): number {
    return this.layers.length;
  }

  /**
   * Check if a layer can be deleted (not the last one, and not the default layer)
   */
  canDeleteLayer(layerId: string): boolean {
    // Cannot delete the default layer
    if (layerId === 'default') return false;
    return this.layers.length > 1 && this.layers.some(l => l.id === layerId);
  }

  /**
   * Notify save state change callback
   */
  private notifySaveStateChanged(): void {
    this.onSaveStateChanged?.(this.isSaving, this.hasUnsavedChanges());
  }

  /**
   * Schedule a debounced save (only if autosave is enabled)
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // Notify that there are unsaved changes
    this.notifySaveStateChanged();

    // Only auto-save if enabled
    if (this.autoSaveEnabled) {
      this.saveTimeout = setTimeout(() => {
        this.saveDirtyLayers();
      }, LayerManager.SAVE_DELAY);
    }
  }

  /**
   * Save all pending changes (server-side only: name and order)
   */
  async saveDirtyLayers(): Promise<void> {
    // If a save is already in progress, wait for it to complete
    // This ensures that callers waiting for layer IDs to be mapped get the correct IDs
    if (this.isSaving && this.currentSavePromise) {
      // Mark that we need another save after the current one completes
      this.pendingSaveAfterCurrent = true;
      // Wait for the current save to finish so IDs are mapped
      await this.currentSavePromise;
      return;
    }

    if (this.dirtyLayers.size === 0 && this.newLayers.size === 0 && this.deletedLayers.size === 0) {
      return;
    }

    this.isSaving = true;
    this.pendingSaveAfterCurrent = false;
    this.notifySaveStateChanged();

    // Store the save promise so other callers can wait for it
    this.currentSavePromise = this.doSave();
    await this.currentSavePromise;
    this.currentSavePromise = null;
  }

  /**
   * Internal save implementation
   */
  private async doSave(): Promise<void> {

    try {
      // 1. Delete layers marked for deletion
      for (const layerId of this.deletedLayers) {
        try {
          await layersApi.delete(this.mapId, layerId);
        } catch (err) {
          console.error(`Failed to delete layer ${layerId}:`, err);
        }
      }
      this.deletedLayers.clear();

      // 2. Create new layers
      const idMapping = new Map<string, string>();
      for (const localId of this.newLayers) {
        const layer = this.layers.find(l => l.id === localId);
        if (layer) {
          try {
            // Resolve parent ID if it was a local ID
            let resolvedParentId = layer.parentId;
            if (resolvedParentId && idMapping.has(resolvedParentId)) {
              resolvedParentId = idMapping.get(resolvedParentId)!;
            }

            const response = await layersApi.create(this.mapId, layer.name, resolvedParentId);
            // Update local ID to server ID
            const serverLayer = response.layer;
            const oldId = layer.id;
            layer.id = serverLayer.id;
            idMapping.set(localId, serverLayer.id);

            // Update localStorage with new ID
            this.updateLocalSettingsId(oldId, serverLayer.id);

            // If order differs, mark as dirty
            if (layer.order !== serverLayer.order) {
              this.dirtyLayers.add(serverLayer.id);
            }
          } catch (err) {
            console.error('Failed to create layer:', err);
          }
        }
      }
      this.newLayers.clear();

      // Update parent IDs that were local IDs
      for (const layer of this.layers) {
        if (layer.parentId && idMapping.has(layer.parentId)) {
          layer.parentId = idMapping.get(layer.parentId)!;
          if (!this.newLayers.has(layer.id)) {
            this.dirtyLayers.add(layer.id);
          }
        }
      }

      // Update active layer ID if it was a local ID
      if (idMapping.has(this.activeLayerId)) {
        this.activeLayerId = idMapping.get(this.activeLayerId)!;
      }

      // Notify about layer ID mappings so props/texts can update their layerIds
      if (idMapping.size > 0 && this.onLayerIdsMapped) {
        this.onLayerIdsMapped(idMapping);
      }

      // 3. Update dirty layers (server-side properties only: name, order, parentId, description)
      for (const layerId of this.dirtyLayers) {
        const layer = this.layers.find(l => l.id === layerId);
        if (layer) {
          try {
            await layersApi.update(this.mapId, layer.id, {
              name: layer.name,
              order: layer.order,
              parentId: layer.parentId,
              description: layer.description
            });
          } catch (err) {
            console.error(`Failed to update layer ${layer.id}:`, err);
          }
        }
      }
      this.dirtyLayers.clear();

      this.notifyLayersChanged();
    } catch (err) {
      console.error('Failed to save layers:', err);
    } finally {
      this.isSaving = false;
      this.notifySaveStateChanged();

      // If another save was requested during this save, trigger it now
      if (this.pendingSaveAfterCurrent) {
        this.pendingSaveAfterCurrent = false;
        this.scheduleSave();
      }
    }
  }

  /**
   * Update localStorage when a layer ID changes (after server creation)
   */
  private updateLocalSettingsId(oldId: string, newId: string): void {
    try {
      const settings = this.loadLocalSettings();
      if (settings[oldId]) {
        settings[newId] = settings[oldId];
        delete settings[oldId];
        localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(settings));
      }
    } catch (err) {
      console.error('Failed to update local settings ID:', err);
    }
  }

  /**
   * Force save immediately
   */
  async forceSave(): Promise<void> {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    await this.saveDirtyLayers();
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.dirtyLayers.size > 0 || this.newLayers.size > 0 || this.deletedLayers.size > 0;
  }

  /**
   * Notify that layers have changed
   */
  private notifyLayersChanged(): void {
    this.onLayersChanged?.();
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
}
